<script setup>
import { ref, computed, watch } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { platformLabel as platformName } from '@/lib/platformLabel'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import PublicGallery from '@/Components/Public/PublicGallery.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import ProductRail from '@/Components/Public/ProductRail.vue'
import BookingRequestForm from '@/Components/Public/BookingRequestForm.vue'
import PriceTierHints from '@/Components/Public/PriceTierHints.vue'
import CheckoutCard from '@/Components/Public/CheckoutCard.vue'
import { usePublicMoney } from '@/composables/usePublicMoney'
import { usePublicDate } from '@/composables/usePublicDate'
import { usePriceQuote } from '@/composables/usePriceQuote'
import DateField from '@/Components/Public/DateField.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const { formatDate } = usePublicDate()
const props = defineProps({
  property:         { type: Object,  required: true },
  base_path:        { type: String,  default: '/property' },
  related:          { type: Array,   default: () => [] },
  checkout_enabled: { type: Boolean, default: false }
})

const photos = props.property.photos?.length ? props.property.photos : []
const eyebrow = computed(() => {
  const parts = [t(`host_portal.property.categories.${props.property.category}`)]
  if (props.property.area) parts.push(props.property.area)
  return parts.join(' · ')
})
const price = computed(() => props.property.price_per_night || props.property.price_per_month)

// "Plan your stay" booking helper — mirrors the tour/catalog plan cards:
// check-in/check-out dates drive a live nightly estimate and feed the
// shared BookingRequestForm.
const { formatMoney } = usePublicMoney()
const today = computed(() => new Date().toLocaleDateString('en-CA')) // YYYY-MM-DD, local
const checkIn = ref('')
const checkOut = ref('')
const adults = ref(1)
const children = ref(0)

function bump(which, delta) {
  if (which === 'adults') adults.value = Math.max(1, adults.value + delta)
  else children.value = Math.max(0, children.value + delta)
}

const nights = computed(() => {
  if (!checkIn.value || !checkOut.value) return 0
  const ms = new Date(checkOut.value) - new Date(checkIn.value)
  if (ms < 0) return 0
  return Math.max(1, Math.round(ms / 86400000))
})
// Local math is the pre-server-quote placeholder only: shown while the
// debounced server quote (which also handles the monthly-rate formula and
// any assigned rate-plan tier) is in flight or hasn't landed yet.
const estimateCents = computed(() => nights.value * (props.property.price_per_night_cents || 0))
const { quote, loading: quoteLoading, schedule: scheduleQuote } = usePriceQuote('property', props.property.id)
watch([ checkIn, checkOut ], () => {
  // Price-on-request VIP listings never have a numeric total — /quote
  // itself 404s for these (Public::QuotesController#show), so skip the
  // fetch entirely rather than firing a call that can only fail.
  if (props.property.price_on_request || props.property.unclaimed) return
  scheduleQuote(checkIn.value && checkOut.value ? { start_on: checkIn.value, end_on: checkOut.value } : null)
}, { immediate: true })
const estimateDisplay = computed(() => {
  if (!quoteLoading.value && quote.value) return formatMoney(quote.value.total_cents)
  return formatMoney(estimateCents.value)
})
const nightsLabel = computed(() => t('public.properties.booking.nights', { n: nights.value }, nights.value))
</script>

<template>
  <Head>
    <title>{{ property.title }} · {{ t('public.head_title') }}</title>
    <meta name="description" :content="property.summary || property.description">
  </Head>

  <BrandHero kind="property" :eyebrow="eyebrow" :title="property.title">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.properties.back') }}</a>
    </template>
    <p v-if="property.summary" class="bhero-lede">{{ property.summary }}</p>
    <span v-if="property.verified" class="bhero-verified">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>{{ t('public.verified') }}
    </span>
    <span v-if="property.luxury" class="bhero-vip">{{ t('public.vip.badge') }}</span>
    <span v-if="property.unclaimed" class="bhero-unclaimed">{{ t('public.unclaimed.badge') }}</span>
    <p v-if="property.price_on_request" class="bhero-price fw-value">{{ t('public.vip.price_on_request') }}</p>
    <p v-else-if="price" class="bhero-price"><span v-if="property.unclaimed" class="bhero-from">{{ t('public.unclaimed.from') }} </span><span class="fw-value">{{ price }}</span></p>
    <div v-if="property.contact_intents && Object.keys(property.contact_intents).length" class="bhero-ctas">
      <LuxButton
        v-for="(url, platform, idx) in property.contact_intents"
        :key="platform"
        :href="url"
        :variant="idx === 0 ? 'solid' : 'line'"
        tone="dark"
        :glyph="platform"
        target="_blank"
        rel="noopener"
      >{{ t('public.write_on', { platform: platformName(platform) }) }}</LuxButton>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <p v-if="property.unclaimed" class="unclaimed-notice">{{ t('public.unclaimed.notice') }}</p>
      <a v-if="property.unclaimed && property.claim_url" :href="property.claim_url" class="unclaimed-claim">{{ t('public.unclaimed.claim_cta') }}</a>
      <div class="layout">
        <PublicGallery :photos="photos" :alt="property.title">
          <template #placeholder>
            <CatalogPlaceholder kind="property" :category="property.category" />
          </template>
        </PublicGallery>

        <div class="info">
          <section v-if="property.description" class="card">
            <h2 class="section-title">{{ t('public.properties.sections.about') }}</h2>
            <p class="prose">{{ property.description }}</p>
          </section>

          <section class="card">
            <h2 class="section-title">{{ t('public.properties.sections.details') }}</h2>
            <dl class="dl">
              <div v-if="property.area"><dt>{{ t('public.properties.fields.area') }}</dt><dd>{{ property.area }}</dd></div>
              <div v-if="property.bedrooms"><dt>{{ t('public.properties.fields.bedrooms') }}</dt><dd>{{ property.bedrooms }}</dd></div>
              <div v-if="property.bathrooms"><dt>{{ t('public.properties.fields.bathrooms') }}</dt><dd>{{ property.bathrooms }}</dd></div>
              <div v-if="property.max_guests"><dt>{{ t('public.properties.fields.max_guests') }}</dt><dd>{{ property.max_guests }}</dd></div>
              <div v-if="property.size_sqm"><dt>{{ t('public.properties.fields.size_sqm') }}</dt><dd>{{ property.size_sqm }}</dd></div>
              <template v-if="property.price_on_request">
                <div v-if="property.priced_per_night"><dt>{{ t('public.properties.fields.price_per_night') }}</dt><dd class="dl__price">{{ t('public.vip.price_on_request') }}</dd></div>
                <div v-else><dt>{{ t('public.properties.fields.price_per_month') }}</dt><dd class="dl__price">{{ t('public.vip.price_on_request') }}</dd></div>
              </template>
              <template v-else>
                <div v-if="property.price_per_night"><dt>{{ t('public.properties.fields.price_per_night') }}</dt><dd class="dl__price">{{ property.price_per_night }}</dd></div>
                <div v-if="property.price_per_month"><dt>{{ t('public.properties.fields.price_per_month') }}</dt><dd class="dl__price">{{ property.price_per_month }}</dd></div>
              </template>
            </dl>
            <PriceTierHints :tiers="property.price_tiers" kind="duration" />
          </section>

          <section v-if="property.features && property.features.length" class="card">
            <h2 class="section-title">{{ t('public.properties.sections.features') }}</h2>
            <ul class="amenities">
              <li v-for="f in property.features" :key="f" class="amenity">{{ t(`public.properties.features.items.${f}`) }}</li>
            </ul>
          </section>

          <section v-if="property.amenities && property.amenities.length" class="card">
            <h2 class="section-title">{{ t('public.properties.sections.amenities') }}</h2>
            <ul class="amenities">
              <li v-for="a in property.amenities" :key="a" class="amenity">{{ t(`host_portal.property.amenities.${a}`) }}</li>
            </ul>
          </section>

          <section v-if="property.blackouts && property.blackouts.length" class="card">
            <h2 class="section-title">{{ t('public.properties.sections.unavailable') }}</h2>
            <ul class="blackouts">
              <li v-for="(b, i) in property.blackouts" :key="i" class="blackout">{{ formatDate(b.start_on) }} → {{ formatDate(b.end_on) }}</li>
            </ul>
          </section>

          <div class="plan-card">
            <h2 class="section-title">{{ t('public.properties.booking.title') }}</h2>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.properties.booking.checkin') }}</span>
              <DateField v-model="checkIn" :min="today" class="plan-date" :placeholder="t('public.pick_date')" />
            </label>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.properties.booking.checkout') }}</span>
              <DateField v-model="checkOut" :min="checkIn || today" class="plan-date" :placeholder="t('public.pick_date')" />
            </label>
            <div class="plan-row">
              <span class="plan-label">{{ t('public.properties.booking.adults') }}</span>
              <span class="stepper">
                <button type="button" class="stepper__btn" :aria-label="t('public.properties.booking.adults')" @click="bump('adults', -1)">−</button>
                <span class="stepper__n fw-value">{{ adults }}</span>
                <button type="button" class="stepper__btn" :aria-label="t('public.properties.booking.adults')" @click="bump('adults', 1)">+</button>
              </span>
            </div>
            <div class="plan-row">
              <span class="plan-label">{{ t('public.properties.booking.children') }}</span>
              <span class="stepper">
                <button type="button" class="stepper__btn" :aria-label="t('public.properties.booking.children')" @click="bump('children', -1)">−</button>
                <span class="stepper__n fw-value">{{ children }}</span>
                <button type="button" class="stepper__btn" :aria-label="t('public.properties.booking.children')" @click="bump('children', 1)">+</button>
              </span>
            </div>
            <p v-if="property.price_on_request" class="plan-total fw-value">{{ t('public.vip.price_on_request') }}</p>
            <template v-else>
              <p v-if="nights > 0 && property.price_per_night_cents" class="plan-total">
                {{ t('public.properties.booking.estimate_total') }}
                <span class="fw-value">≈ {{ estimateDisplay }}</span>
                <span class="plan-days">({{ nightsLabel }})</span>
              </p>
              <p class="plan-caveat">{{ t('public.properties.booking.estimate_caveat') }}</p>
            </template>
            <h3 class="plan-subtitle">{{ t('public.booking_request.title') }}</h3>
            <p v-if="property.unclaimed" class="unclaimed-hint">{{ t('public.unclaimed.request_hint') }}</p>
            <BookingRequestForm
              bookable-type="property"
              :bookable-id="property.id"
              :details="{ start_on: checkIn, end_on: checkOut, adults, children }"
              :disabled="!checkIn || !checkOut"
            />
          </div>

          <CheckoutCard
            v-if="checkout_enabled && !property.price_on_request && !property.unclaimed"
            bookable-type="property"
            :bookable-id="property.id"
            :details="{ start_on: checkIn, end_on: checkOut, adults, children }"
            :disabled="!checkIn || !checkOut"
            :quote="quote"
            :quote-loading="quoteLoading"
          />
        </div>
      </div>
    </div>
  </section>

  <section
    v-if="related.length"
    class="related"
  >
    <div class="related__inner">
      <ProductRail
        :title="t('public.properties.related')"
        kind="property"
        :items="related"
        :item-base="base_path"
        :view-all-href="base_path"
      />
    </div>
  </section>
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 52ch; opacity: 0.94; }
.bhero-verified {
  display: inline-flex; align-items: center; gap: 0.35rem; margin: 1rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 16%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 35%, transparent);
}
.bhero-verified svg { width: 1rem; height: 1rem; }
.bhero-vip {
  display: inline-flex; align-items: center; margin: 1rem 0.4rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: var(--ink); color: var(--warn); border: 1px solid var(--warn);
}
.bhero-unclaimed {
  display: inline-flex; align-items: center; margin: 1rem 0.4rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 12%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 30%, transparent);
  opacity: 0.85;
}
.bhero-price { margin: 1.1rem 0 0; font-size: clamp(1.6rem, 3vw, 2.1rem); }
.bhero-from { font-size: 1rem; opacity: 0.8; }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
@media (min-width: 880px) { .layout { grid-template-columns: 1.4fr 1fr; gap: 2.5rem; } }
.unclaimed-notice {
  margin: 0 0 0.75rem; padding: 0.85rem 1rem;
  background: var(--surfaceAlt); border: 1px solid var(--line); border-radius: var(--radius-table, 0.75rem);
  color: var(--inkSoft); font-size: 0.9rem; line-height: 1.5;
}
.unclaimed-claim {
  display: inline-block; margin: 0 0 1.5rem; color: var(--accent); font-weight: 700; text-decoration: none; font-size: 0.9rem;
}
.unclaimed-claim:hover { text-decoration: underline; }
.unclaimed-hint { margin: 0.4rem 0 0.75rem; font-size: 0.82rem; color: var(--muted); }
.info { display: grid; gap: 1rem; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.dl { display: grid; gap: 0.55rem; margin: 0; }
.dl > div { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem; align-items: baseline; }
.dl dt { color: var(--muted); font-size: 0.875rem; }
.dl dd { margin: 0; color: var(--ink); font-size: 1rem; }
.dl__price { color: var(--accent); font-weight: 700; }
.amenities { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 0.4rem; }
.amenity { padding: 0.3rem 0.75rem; border: 1px solid var(--line); border-radius: 999px; font-size: 0.875rem; color: var(--inkSoft); }
.blackouts { list-style: none; padding: 0; margin: 0; display: grid; gap: 0.3rem; }
.blackout { font-size: 0.9rem; color: var(--inkSoft); }
.plan-card {
  padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.plan-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 0.85rem; }
.plan-label { color: var(--inkSoft); font-size: 0.95rem; }
.plan-date {
  border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem);
  background: var(--bg); color: var(--ink); padding: 0.4rem 0.6rem; font: inherit;
  color-scheme: light;
}
.stepper { display: inline-flex; align-items: center; gap: 0.5rem; }
.stepper__btn {
  width: 2rem; height: 2rem; border-radius: 999px;
  border: 1px solid var(--line); background: var(--surface); color: var(--ink);
  font-size: 1.1rem; line-height: 1; cursor: pointer; display: grid; place-items: center;
}
.stepper__btn:hover { border-color: var(--accent); color: var(--accent); }
.stepper__n { min-width: 1.5rem; text-align: center; }
.plan-total { margin: 1rem 0 0.2rem; font-size: 1.05rem; color: var(--ink); }
.plan-days { color: var(--muted); font-size: 0.9rem; margin-left: 0.3rem; }
.plan-caveat { margin: 0.3rem 0 0; font-size: 0.8rem; color: var(--muted); }
.plan-subtitle { margin: 1.1rem 0 0; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); }
.related { background: var(--surface); border-top: 1px solid var(--line); }
.related__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem); }
</style>
